import { readFileSync, writeFileSync, existsSync } from 'node:fs';
import { join } from 'node:path';
import { homedir } from 'node:os';
import { isAuthenticated, getAccessToken } from '../auth/stub.js';

const SYNKRO_DIR = join(homedir(), '.synkro');
const CONFIG_PATH = join(SYNKRO_DIR, 'config.env');

function readConfigEnv(): Record<string, string> {
  if (!existsSync(CONFIG_PATH)) return {};
  const out: Record<string, string> = {};
  for (const line of readFileSync(CONFIG_PATH, 'utf-8').split('\n')) {
    const t = line.trim();
    if (!t || t.startsWith('#')) continue;
    const eq = t.indexOf('=');
    if (eq > 0) out[t.slice(0, eq).trim()] = t.slice(eq + 1).trim().replace(/^['"]|['"]$/g, '');
  }
  return out;
}

function updateConfigValue(key: string, value: string): void {
  if (!existsSync(CONFIG_PATH)) {
    console.error('No config found. Run `synkro install` first.');
    process.exit(1);
  }
  const lines = readFileSync(CONFIG_PATH, 'utf-8').split('\n');
  const pattern = new RegExp(`^${key}=`);
  let found = false;
  const updated = lines.map((line) => {
    if (pattern.test(line.trim())) {
      found = true;
      return `${key}='${value}'`;
    }
    return line;
  });
  if (!found) updated.splice(updated.length - 1, 0, `${key}='${value}'`);
  writeFileSync(CONFIG_PATH, updated.join('\n'), 'utf-8');
}

// The canonical 3-state grading location, derived from the two routing vars the
// hooks actually read — keep this in lockstep with route()/selectGrader() in
// hookScriptsTs.ts. 'byok' (own API key) wins; else cloud worker pool; else
// on-device workers. Single source of truth, so the printout can't drift.
function resolveInferenceMode(cfg: Record<string, string>): 'local' | 'cloud' | 'byok' {
  if ((cfg.SYNKRO_GRADING_MODE || 'local') === 'byok') return 'byok';
  if (cfg.SYNKRO_DEPLOY_LOCATION === 'cloud') return 'cloud';
  return 'local';
}

// After a grading/storage change, bring the local container in line with the
// new modes — this is what makes switching seamless (no separate install step).
// Cloud-only (BYOK grading + cloud storage) needs no container; every other
// combo needs it. Only acts when crossing that boundary; wrapped so a Docker
// hiccup never fails the config write itself.
async function reconcileContainer(): Promise<void> {
  const cfg = readConfigEnv();
  const cloudOnly =
    (cfg.SYNKRO_GRADING_MODE || 'local') === 'byok' &&
    (cfg.SYNKRO_STORAGE_MODE || 'local') === 'cloud';

  try {
    const { dockerInstall, dockerSafeStop, readContainerConfig, assertDockerAvailable, waitForContainerReady } =
      await import('../local-cc/dockerInstall.js');
    const existing = readContainerConfig();

    if (cloudOnly && existing) {
      console.log('Cloud-only mode — the local container is no longer needed; stopping it...');
      await dockerSafeStop();
      console.log('  ✓ container stopped (grading → BYOK, telemetry → cloud).');
    } else if (!cloudOnly && !existing) {
      assertDockerAvailable();
      console.log('This mode needs the Synkro container — provisioning...');
      const { detectGitRepo } = await import('./install.js');
      await dockerInstall({ claudeWorkers: 8, cursorWorkers: 0, connectedRepo: detectGitRepo() ?? undefined });
      const ready = await waitForContainerReady(60_000);
      console.log(ready
        ? '  ✓ container running.'
        : '  ⚠ container did not pass its health check — see `docker logs synkro-server`.');
    }
    // else: the container's presence already matches the mode — nothing to do.
  } catch (err) {
    console.warn(`  ⚠ Container reconcile skipped: ${(err as Error).message}`);
    console.warn('    Run `synkro install` to (re)provision the container if needed.');
  }
}

export async function configCommand(args: string[]): Promise<void> {
  if (args.length === 0) {
    const config = readConfigEnv();
    console.log('Synkro config:\n');
    console.log(`  inference:  ${resolveInferenceMode(config)}`);
    console.log(`  storage:    ${config.SYNKRO_STORAGE_MODE || 'local'}`);
    console.log(`  tier:       ${config.SYNKRO_TIER || 'pro'}`);
    console.log(`  gateway:    ${config.SYNKRO_GATEWAY_URL || 'https://api.synkro.sh'}`);
    console.log(`  version:    ${config.SYNKRO_VERSION || '?'}`);
    console.log(`\nTo change:`);
    console.log(`  synkro config --inference <local|cloud|byok>  — where grading runs`);
    console.log(`      local  — Claude/Cursor workers on this machine (your subscription)`);
    console.log(`      cloud  — Claude/Cursor workers in the Synkro cloud pool`);
    console.log(`      byok   — graded via your own provider API key`);
    console.log(`  synkro config storage <local|cloud>           — where telemetry is stored`);
    return;
  }

  // User-owned data axes — written to config.env; the hooks load config.env at
  // process start, so the change takes effect on the next graded tool call.
  if (args[0] === 'grading') {
    const value = args[1];
    if (value !== 'local' && value !== 'byok') {
      console.error('Usage: synkro config grading <local|byok>');
      process.exit(1);
    }
    updateConfigValue('SYNKRO_GRADING_MODE', value);
    console.log(`✓ Grading mode set to '${value}'.`);
    if (value === 'byok') {
      console.log('  BYOK grading uses your own provider key — register one in the');
      console.log('  dashboard under Settings → Provider Keys if you have not already.');
    }
    await reconcileContainer();
    return;
  }
  if (args[0] === 'storage') {
    const value = args[1];
    if (value !== 'local' && value !== 'cloud') {
      console.error('Usage: synkro config storage <local|cloud>');
      process.exit(1);
    }
    updateConfigValue('SYNKRO_STORAGE_MODE', value);
    console.log(`✓ Storage mode set to '${value}'.`);
    await reconcileContainer();
    return;
  }

  let inferenceValue: string | undefined;
  for (const a of args) {
    if (a.startsWith('--inference=')) inferenceValue = a.slice('--inference='.length);
    else if (a === '--inference' && args.indexOf(a) + 1 < args.length) inferenceValue = args[args.indexOf(a) + 1];
  }

  if (!inferenceValue || !['local', 'cloud', 'byok'].includes(inferenceValue)) {
    console.error('Usage: synkro config --inference <local|cloud|byok>');
    process.exit(1);
  }

  if (!isAuthenticated()) {
    console.error('Not authenticated. Run `synkro login` first.');
    process.exit(1);
  }

  const token = getAccessToken();
  const config = readConfigEnv();
  const gatewayUrl = (config.SYNKRO_GATEWAY_URL || 'https://api.synkro.sh').replace(/\/$/, '');

  // Persist the canonical mode to the DB so the dashboard reflects it.
  try {
    const resp = await fetch(`${gatewayUrl}/api/v1/cli/me`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ inference_mode: inferenceValue }),
    });
    if (!resp.ok) {
      const errText = await resp.text().catch(() => '');
      console.error(`Failed to update: ${resp.status} ${errText.slice(0, 200)}`);
      process.exit(1);
    }
  } catch (err) {
    console.error(`Failed to reach server: ${(err as Error).message}`);
    process.exit(1);
  }

  // Mirror the mode into the two routing vars the hooks read, so the next graded
  // tool call actually runs where the dashboard now says it does.
  if (inferenceValue === 'byok') {
    updateConfigValue('SYNKRO_GRADING_MODE', 'byok');
    updateConfigValue('SYNKRO_DEPLOY_LOCATION', 'local');
  } else if (inferenceValue === 'cloud') {
    updateConfigValue('SYNKRO_GRADING_MODE', 'local');
    updateConfigValue('SYNKRO_DEPLOY_LOCATION', 'cloud');
  } else {
    updateConfigValue('SYNKRO_GRADING_MODE', 'local');
    updateConfigValue('SYNKRO_DEPLOY_LOCATION', 'local');
  }

  console.log(`✓ Inference set to '${inferenceValue}'.`);
  if (inferenceValue === 'byok') {
    console.log('  BYOK grading uses your own provider key — register one in the');
    console.log('  dashboard under Settings → Provider Keys if you have not already.');
  } else if (inferenceValue === 'cloud') {
    console.log('  Grading runs on the Synkro cloud worker pool.');
  }

  // local/byok grade through the on-device container; cloud uses the hosted
  // pool, so it doesn't need (and shouldn't provision) a local container.
  if (inferenceValue !== 'cloud') await reconcileContainer();
}
